/** Wording and number formats for Educational Programs. Plain words, no em dashes, no percentages. */
import { ProgramCard, ProgramSession, Stage } from './types';

export const TYPE_LABEL: Record<string, string> = {
  camp_course: 'Camp or course',
  research: 'Research',
  internship: 'Internship',
  competition: 'Competition',
  college_credit: 'College credit',
  online_course: 'Online course',
  travel: 'Travel',
};

/** How competitive. Neutral labels only; GradMap never states anyone's chances. */
export const COMPETITIVENESS: Record<string, { label: string; tone: '' | 'a' | 'ok'; help: string }> = {
  highly_selective: { label: 'Highly selective', tone: 'a', help: 'The program publishes an acceptance rate of 15% or lower' },
  selective: { label: 'Selective', tone: 'a', help: 'The program publishes an acceptance rate between 15% and 40%' },
  application_required: { label: 'Application required', tone: '', help: 'Essays, recommendations, an interview or similar' },
  open_enrollment: { label: 'Open enrollment', tone: 'ok', help: 'Anyone who meets the eligibility can sign up' },
};

export const PLAN_LABEL: Record<string, string> = {
  interested: 'Interested',
  applying: 'Applying',
  applied: 'Already applied',
  attending: 'Attending',
};

export const STAGES: [Stage, string][] = [
  ['researching', 'Researching'],
  ['applying', 'Applying'],
  ['submitted', 'Submitted'],
  ['decision', 'Decision'],
];

export const DECISION_LABEL: Record<string, string> = {
  accepted: 'Accepted',
  waitlisted: 'Waitlisted',
  not_accepted: 'Not accepted',
  withdrawn: 'Withdrew',
};

export const RECOMMENDER_STATUS: [string, string][] = [
  ['not_asked', 'Not asked yet'],
  ['asked', 'Asked'],
  ['agreed', 'Agreed'],
  ['sent', 'Letter sent'],
  ['declined', 'Said no'],
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const iso = (v: unknown): string | null => {
  if (!v) return null;
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
};
export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
/** "Nov 4" */
export const md = (v: unknown) => {
  const s = iso(v);
  return s ? `${MONTHS[+s.slice(5, 7) - 1]} ${+s.slice(8, 10)}` : '';
};
/** "Nov 4, 2026" */
export const mdy = (v: unknown) => {
  const s = iso(v);
  return s ? `${md(s)}, ${s.slice(0, 4)}` : '';
};
export const isFuture = (v: unknown) => {
  const s = iso(v);
  return !!s && s >= todayIso();
};
export const addDays = (isoDate: string, days: number) => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

export const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

/** Shown when a program has not posted its cost (same words as www.gradmap.com). */
export const UNKNOWN_COST = 'Cost on program website';

/** "$1,200 to $1,500", "Up to $3,800" when the low end is 0, or one price. */
export const rangeText = (a: number, b: number) => (a === 0 ? `Up to ${money(b)}` : a === b ? money(a) : `${money(a)} to ${money(b)}`);

export const programName = (p: { displayName?: string | null; programName?: string | null; name?: string | null }) =>
  p.displayName || p.programName || p.name || 'Program';

/** Next cycle year for "2027 deadline not posted". */
export const nextCycleYear = () => {
  const d = new Date();
  return d.getMonth() >= 8 ? d.getFullYear() + 1 : d.getFullYear();
};

export function deadlineText(kind: string | null, next: string | null, lastYear: string | null): string {
  if (next && isFuture(next)) return `Deadline ${mdy(next)}`;
  if (kind === 'rolling') return 'Rolling admission';
  if (kind === 'until_full') return 'Open until full';
  if (kind === 'ongoing') return 'Open all year';
  return lastYear ? `${nextCycleYear()} deadline not posted · last year ${md(lastYear)}` : `${nextCycleYear()} deadline not posted`;
}

export function cardDeadline(p: ProgramCard): string {
  const s = p.sessions;
  const next = [s?.next_priority_deadline, s?.next_deadline, p.deadlineDate].map(iso).filter((d): d is string => !!d && isFuture(d)).sort()[0] ?? null;
  return deadlineText(p.deadlineKind, next, p.lastYearDeadlineDate);
}

/** null when free, UNKNOWN_COST when unknown. */
export function priceText(p: ProgramCard): { free: boolean; text: string | null } {
  const s = p.sessions;
  if (s?.price_status === 'free' || (p.isFree && !s)) return { free: true, text: null };
  const lo = s?.cost_low ?? null;
  const hi = s?.cost_high ?? null;
  if (lo == null && hi == null) return { free: false, text: UNKNOWN_COST };
  if (lo === 0 && (hi ?? 0) === 0) return { free: true, text: null };
  const a = lo ?? hi!;
  const b = hi ?? lo!;
  return { free: false, text: rangeText(a, b) };
}

/** Price across a program's sessions (the program page), falling back to the program's own fields. */
export function sessionsPrice(sessions: ProgramSession[], p: ProgramCard): { free: boolean; text: string | null } {
  if (!sessions.length) return priceText(p);
  if (sessions.every((s) => s.is_free || s.price_status === 'free')) return { free: true, text: null };
  const lows = sessions.map((s) => (s.is_free ? 0 : s.cost_min ?? s.cost_max)).filter((v): v is number => v != null);
  const highs = sessions.map((s) => (s.is_free ? 0 : s.cost_max ?? s.cost_min)).filter((v): v is number => v != null);
  if (!lows.length) return { free: false, text: UNKNOWN_COST };
  const a = Math.min(...lows);
  const b = Math.max(...highs);
  return { free: false, text: rangeText(a, b) };
}

export function sessionPrice(s: ProgramSession): { free: boolean; text: string | null } {
  if (s.is_free || s.price_status === 'free') return { free: true, text: null };
  if (s.cost_min == null && s.cost_max == null) return { free: false, text: UNKNOWN_COST };
  const a = s.cost_min ?? s.cost_max!;
  const b = s.cost_max ?? s.cost_min!;
  return { free: false, text: rangeText(a, b) };
}

export function weeksText(min: unknown, max: unknown): string {
  const a = min == null || min === '' ? null : Number(min);
  const b = max == null || max === '' ? a : Number(max);
  if (a == null || !isFinite(a)) return '';
  const r = (v: number) => Math.round(v * 10) / 10;
  const one = (v: number) => (v < 1 ? `${Math.max(1, Math.round(v * 7))} days` : `${r(v)} ${v === 1 ? 'week' : 'weeks'}`);
  if (a === 0) return b != null && b > 1 ? `Up to ${r(b)} weeks` : 'Up to 1 week';
  if (b == null || a === b) return one(a);
  return a < 1 && b < 1 ? `${one(a)} to ${one(b)}` : `${r(a)} to ${r(b)} weeks`;
}

export function deliveryText(modes: (string | null)[]): string {
  const d = new Set(modes.filter(Boolean));
  if (d.has('online_or_in_person') || (d.has('online') && d.size > 1)) return 'In person or online';
  if (d.has('hybrid')) return 'Hybrid';
  if (d.has('online')) return 'Online';
  return 'In person';
}

export function housingText(h: string | null): string {
  if (h === 'residential') return 'Residential';
  if (h === 'commuter') return 'Commuter';
  if (h === 'both') return 'Residential or commuter';
  return '';
}

/** "Camp or course · In person · Kingston, Rhode Island · 6 to 8 weeks" */
export function cardFacts(p: ProgramCard): string {
  const s = p.sessions;
  const delivery = deliveryText(s?.delivery?.length ? s.delivery : [p.deliveryMode]);
  const states = s?.states ?? (p.stateName ? [p.stateName] : []);
  let place = '';
  if (p.nearest) place = p.nearest.miles < 1 ? 'Under 1 mile away' : p.nearest.miles === 1 ? 'About 1 mile away' : `About ${p.nearest.miles} miles away`;
  else if (delivery !== 'Online') {
    place = states.length > 1 ? 'Several locations' : p.city && states[0] ? `${p.city}, ${states[0]}` : states[0] || '';
  }
  return [p.programType ? TYPE_LABEL[p.programType] : '', delivery, place, weeksText(p.durationWeeksMin, p.durationWeeksMax)]
    .filter(Boolean)
    .join(' · ');
}

/** What the application asks for, from the program's requirement counts. */
export function asksFor(p: ProgramCard): string {
  const parts: string[] = [];
  const essays = Number(p.essaysCount ?? 0);
  const recs = Number(p.teacherRecommendationsCount ?? 0) + Number(p.generalRecommendationsCount ?? 0);
  if (essays) parts.push(essays === 1 ? 'an essay' : `${essays} essays`);
  if (recs) parts.push(recs === 1 ? 'a recommendation' : `${recs} recommendations`);
  if (p.transcriptRequired) parts.push('a transcript');
  if (p.resume) parts.push('a resume');
  if (p.interviewRequired) parts.push('an interview');
  if (!parts.length) return '';
  const last = parts.pop()!;
  return parts.length ? `${parts.join(', ')} and ${last}` : last;
}

export const gradesText = (g: number[] | null) => (g && g.length ? `Grades ${[...g].sort((a, b) => a - b).join(', ')}` : '');
